import { Plan, Bolton, OrderCredit } from '@smarty-nx/data'

import {
  buildAddonProductData,
  buildAddonTransactionData,
} from './buildEventData'
import { buildCartData, buildProductData, buildTransactionData } from './index'
import { DATA_LAYER_EVENTS, DATA_LAYER_JOURNEY_TYPES } from '../constants'
import { buildCart, moduleEvent, updateDmpgSmdl, buildUser } from '../services'
import {
  AddonProductObject,
  FormEventProps,
  ProductObject,
  DataLayerEvent,
  SmdlScreen,
} from '../types'

/** Pushes an event to the data layer */
export const triggerEvent = (event: DataLayerEvent) => {
  window.smDl.events.push(event)
}

// Accepts event name (required) and formData - form name and fields
export const triggerFormEvent = ({
  eventName,
  formData,
  error = null,
  userObject,
}: FormEventProps) => {
  triggerEvent({
    event: eventName,
    form: {
      id: formData.id,
      ...(formData.name && { name: formData.name }),
      fields: formData.fields || [],
      // Defaulted errors to an empty array in case of numerous errors.
      // single strings get passed in as ['string']
      error,
    },
    user: userObject,
  })
}

/** v2 cart event function - builds cart based on v2 Plan structure */
export const triggerCartEvent = (
  eventName: string,
  plan: Plan,
  checkoutStep: number,
  checkoutType: DATA_LAYER_JOURNEY_TYPES
) => {
  triggerEvent({
    event: eventName,
    // uses the old buildCart function
    cart: buildCartData(plan, checkoutStep, checkoutType),
  })
}

/**
 * Function to trigger an event with cart data - mostly used on order journeys.
 * The buildCart function takes data attributes from components
 */
export const triggerOrderEvent = (eventName: string) => {
  triggerEvent({
    event: eventName,
    /** uses the old buildCart function for now - only on PayPal journey */
    cart: buildCart(document),
  })
}

/**
 * Function to trigger an event with cart data - mostly used on order journeys.
 * The buildCart function takes data attributes from components
 */
export const triggerProductEvent = (eventName: string, plan: Plan) => {
  triggerEvent({
    event: eventName,
    // uses the old buildCart function
    product: buildProductData(plan),
  })
}

/**
 * Function to trigger an event with add-on cart data.
 */
export const triggerAddonProductEvent = (eventName: string, addon: Bolton) => {
  triggerEvent({
    event: eventName,
    product: buildAddonProductData(addon),
  })
}

/**
 * Function to trigger an event with custom cart data.
 */
export const triggerCustomProductEvent = (
  eventName: string,
  product: ProductObject | AddonProductObject
) => {
  triggerEvent({
    event: eventName,
    product,
  })
}

/**
 * New function to trigger a manual screen load event.
 * It accepts a screen object with page name and type.
 * We can take this from PAGE_CONSTANTS or the journey state objects in
 * journeyStages.js (we should replace these with PAGE_CONSTANTS as we rebuild)
 */
export const triggerScreenLoadEvent = (screenObject: SmdlScreen) => {
  const event = {
    event: DATA_LAYER_EVENTS.SCREEN_LOAD,
    screen: {
      name: screenObject.name,
      type: screenObject.type,
      ...(screenObject.order_type && { order_type: screenObject.order_type }),
      ...(screenObject.lead_generator && {
        lead_generator: screenObject.lead_generator,
      }),
    },
    user: buildUser(),
  }
  triggerEvent(event)
}

/** Build and trigger an event with no extra data */
export const triggerSimpleEvent = (eventName: string) => {
  triggerEvent({ event: eventName })
}

export const triggerTransactionEvent = ({
  id,
  orderCredit,
  plan,
  checkoutType,
  portInOption,
}: {
  id: string | null
  orderCredit: OrderCredit
  plan: Plan
  checkoutType: DATA_LAYER_JOURNEY_TYPES
  portInOption?: string
}) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
    transaction: buildTransactionData({
      id,
      orderCredit,
      plan,
      checkoutType,
      portInOption,
    }),
  })
}

export const triggerPlanCardModuleEvent = (
  parentElement: Document | HTMLElement,
  slug: string
) => {
  const output = moduleEvent(parentElement, slug)
  updateDmpgSmdl(output)
}

export const triggerAddonTransactionEvent = (
  id: string,
  orderCredit: OrderCredit,
  bolton: Bolton
) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
    ...buildAddonTransactionData(id, orderCredit, bolton),
  })
}

export const triggerPortingEvent = (
  eventName: string,
  option: string,
  status: string
) => {
  triggerEvent({
    event: eventName,
    user: {
      ...buildUser(),
      porting: {
        option,
        status,
      },
    },
  })
}

export const triggerGroupMemberPortingEvent = (
  eventName: string,
  option: string,
  status: string
) => {
  triggerEvent({
    event: eventName,
    user: {
      ...buildUser(),
      porting: {
        groupMember: {
          option,
          status,
        },
      },
    },
  })
}
